import { TemplateResult } from 'lit';
import type { CoreShellPorts } from '../../../../../routing/domain/CoreShellPorts.ts';
import { SHELL_SYNC_NAV_HREF } from '@src/modules/shared/app-context/domain/shellNavRoutes.ts';
import {
  isOfflineServiceWorkerUpdatePending,
  OFFLINE_SW_UPDATE_AVAILABLE_EVENT,
} from '@core/domain/offlineServiceWorkerUpdateEvents.ts';
import { LitComponent } from './LitComponent.ts';
import { layoutComponentTemplate } from './layoutComponentTemplate.ts';
import { runLayoutLogoutAndRedirect } from './layoutLogoutFlow.ts';

export class LayoutComponent extends LitComponent {
  shellPorts!: CoreShellPorts;
  private showOfflineUpdateBanner = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.syncOfflineUpdateBannerVisibility();
    globalThis.addEventListener(OFFLINE_SW_UPDATE_AVAILABLE_EVENT, this.onOfflineSwUpdateAvailable);
    globalThis.addEventListener('navigate', this.onShellNavigate);
    globalThis.addEventListener('popstate', this.onShellNavigate);
  }

  disconnectedCallback(): void {
    globalThis.removeEventListener(OFFLINE_SW_UPDATE_AVAILABLE_EVENT, this.onOfflineSwUpdateAvailable);
    globalThis.removeEventListener('navigate', this.onShellNavigate);
    globalThis.removeEventListener('popstate', this.onShellNavigate);
    super.disconnectedCallback();
  }

  private readonly onOfflineSwUpdateAvailable = (): void => {
    this.syncOfflineUpdateBannerVisibility();
  };

  private readonly onShellNavigate = (): void => {
    this.syncOfflineUpdateBannerVisibility();
  };

  private isOnSyncUpdateTab(): boolean {
    return (
      globalThis.location.pathname === '/sync' &&
      new URLSearchParams(globalThis.location.search).get('tab') === 'update'
    );
  }

  private syncOfflineUpdateBannerVisibility(): void {
    const next = isOfflineServiceWorkerUpdatePending() && !this.isOnSyncUpdateTab();
    if (this.showOfflineUpdateBanner !== next) {
      this.showOfflineUpdateBanner = next;
      this.update();
    }
  }

  private readonly handleGoToOfflineUpdate = (): void => {
    this.shellPorts.navigation.navigate(SHELL_SYNC_NAV_HREF);
    globalThis.setTimeout(() => this.syncOfflineUpdateBannerVisibility(), 0);
  };

  private readonly handleLogout = (): void => {
    void runLayoutLogoutAndRedirect(this.shellPorts);
  };

  protected getTemplate(): TemplateResult {
    return layoutComponentTemplate({
      appTitle: this.shellPorts.shellUi.getAppDisplayTitle(),
      shellPorts: this.shellPorts,
      showOfflineUpdateBanner: this.showOfflineUpdateBanner,
      onGoToOfflineUpdate: this.handleGoToOfflineUpdate,
      onLogout: this.handleLogout,
    });
  }
}

customElements.define('layout-component', LayoutComponent);
